// jscs:disable requireTrailingComma
/* global $ */
/* global console */

import SGActionCommand from '../app/sg-actions/sg-action-command';
import TaggedLogger from '../common/debug/tagged-logger';

/**
 * Object representation of URI(RFC2396) string
 * Made for general purpose. Feel free to extend it for your needs.
 *
 * @param {string} url url to creating SGLink from
 * @returns {SGLink}
 * @constructor
 */
const SGLink = function (url) {
  const that = this;
  const logger = new TaggedLogger('SGLink', 'color:pink');

  /**
   * Parses urlToSanitize string
   *
   * @param {string} incomingUrl urlToSanitize to parse
   * @private
   */
  function parseUrl(incomingUrl) {
    let urlToSanitize = incomingUrl;
    if (!urlToSanitize) {
      urlToSanitize = '';
    }

    const commonSchemas = [
      'http', 'https', 'tel', 'mailto',
    ];

    // Based on the regex in RFC2396 Appendix B.
    const parser = /^(?:([^:/?#]+):)?(?:\/\/([^/?#]*))?([^?#]*)(?:\?([^#]*))?(?:#(.*))?/;
    const result = urlToSanitize.match(parser);

    if (!that.isDeepLink && result[1] && commonSchemas.indexOf(result[1]) === -1) {
      that.isDeepLink = true;
      const scheme = `${result[1]}://`;
      /*
        add slash so that we can parse the attributes properly
        (for shopgate-standalone://cart we would get e.g. "authority: cart" which is wrong)
       */
      urlToSanitize = urlToSanitize.replace(scheme, `${scheme}/`);

      parseUrl(urlToSanitize);
      return;
    }

    that.scheme = result[1] || '';
    that.authority = result[2] || '';
    that.path = result[3] || '';
    that.query = result[4] || '';
    that.fragment = result[5] || '';

    if (that.query) {
      const queryParts = that.query.split('&');
      const queryPartsLength = queryParts.length;
      // clearing params
      that.setParams({});
      for (let i = 0; i < queryPartsLength; i += 1) {
        const queryPair = queryParts[i].split('=');
        that.setParam(queryPair[0], queryPair[1]);
      }
    }

    if (that.path) {
      const pathSplitted = that.path.replace('/php/shopgate', '').split('/');
      that.action = pathSplitted[0] || '';
      that.splittedPath = pathSplitted;

      if (pathSplitted[0] === '/' || pathSplitted[0] === '') {
        that.action = pathSplitted[1] || '';
        that.splittedPath.shift();
      }
    }
  }

  /**
   * Encode the url and takes care of double encoding
   * @param {string} string - string to be encoded
   * @returns {string} encoded string
   */
  function encodeURISafe(string) {
    let decoded;

    try {
      decoded = decodeURI(string);
    } catch (e) {
      logger.info('error decoding url', string);
      return string;
    }

    if (decoded !== string) {
      return string;
    }

    return encodeURI(string);
  }

  /**
   * Encode the url with encodeURIComponent and
   * takes care of double encoding
   *
   * @param {string} string - string to be encoded
   * @returns {*}
   */
  function encodeURIComponentSafe(string) {
    const decoded = decodeURIComponent(string);

    if (decoded !== string) {
      return string;
    }

    return encodeURIComponent(string);
  }

  /**
   * Constructor
   * @private
   */
  function init() {
    that.url = url;

    parseUrl(url);
  }

  /**
   * Returns "safe-to-use-for-mapping" action
   *
   * @returns {string}
   * @private
   */
  function getSanitizedAction() {
    let action = that.action;

    if (!action || action === 'start' || action === '/') {
      action = 'index';
    }

    if (!that.actionMapping[action]) {
      action = 'default';
    }

    return action;
  }

  that.url = ''; // Complete url string
  that.scheme = ''; // Any scheme we support (ex. https|shopgate-{$number}|sgapi)
  that.authority = '';
  that.path = '';
  that.splittedPath = [];
  that.query = '';
  that.fragment = '';
  that.action = ''; // Endpoint - safe for shopgate (ex. no endpoint is converted to "index")
  that.params = {};
  that.isDeepLink = false;
  that.isSgApiLink = false;

  /**
   * Converts the object to array of SGActionCommand
   * Will not work for regular urls, just for internal ones.
   * Converts all urls to sgapi ones.
   *
   * @returns {SGActionCommand[]}
   */
  that.toCommands = function (title = null) {
    const cmds = [];
    const sgApiLink = that.toSgApiLink().toString();
    const actionMapping = that.getActionMapping();

    if (actionMapping.hasOwnProperty('sgActionCommand')) {
      cmds.push(new SGActionCommand(actionMapping.sgActionCommand));

      return cmds;
    }

    const openPageParams = {
      src: sgApiLink,
      targetTab: actionMapping.targetTab,
      navigationBarParams: {
        type: actionMapping.navigationType,
      },
      previewSrc: 'sgapi:page_preview',
    };
    if (title) {
      openPageParams.title = title;
    }

    const openPageCommand = new SGActionCommand(
      'openPage',
      openPageParams
    );

    const showTabCommand = new SGActionCommand(
      'showTab',
      {
        targetTab: actionMapping.targetTab,
      }
    );

    const popTabToRootCommand = new SGActionCommand(
      'popTabToRoot',
      {
        targetTab: actionMapping.targetTab,
      }
    );

    cmds.push(showTabCommand);

    if (actionMapping.popTabToRoot) {
      cmds.push(popTabToRootCommand);
    } else {
      cmds.push(openPageCommand);
    }

    return cmds;
  };

  /**
   * Returns true if current action
   * is root page (first page in tab stack).
   *
   * @returns {*|boolean}
   */
  that.isRootPage = function () {
    const mapping = this.actionMapping[getSanitizedAction()];

    return (mapping.hasOwnProperty('popTabToRoot') || mapping.hasOwnProperty('sgActionCommand'));
  };

  /**
   * Converts the SGLink object to have the correct Shopgate Api link properties
   * @returns {SGLink}
   */
  that.toSgApiLink = function () {
    const link = new SGLink(that.toString());

    link.scheme = 'sgapi:';
    link.isSgApiLink = true;
    link.path = (link.path.indexOf('/') === 0) ? link.path.slice(1, link.path.length) : link.path;

    if (!link.path) {
      link.path = 'index';
    }

    return link;
  };

  /**
   * Converts the object to string
   *
   * @returns {string}
   */
  that.toString = function () {
    let outputUrl = '';
    const notNavigatorSchema =
            [
              'mailto',
              'tel',
            ].indexOf(that.scheme) > -1;

    if (that.scheme) {
      outputUrl += that.scheme;

      // Sgapi-links don't need further scheme parsing since the scheme is 'sgapi:' and that's it.
      if (!(that.isSgApiLink || that.scheme === 'sgapi')) {
        if (notNavigatorSchema) {
          if (that.scheme.indexOf(':') === -1) {
            outputUrl += ':';
          }
        } else {
          if (that.scheme.indexOf(':/') === -1) {
            // If the scheme already contains :/ we don't want to add it again
            outputUrl += ':/';
            that.scheme = outputUrl;
          }
          if (!that.isDeepLink) {
            outputUrl += '/';
          }
        }
      } else if (that.scheme.indexOf(':') === -1) {
        outputUrl += ':';
      }
    }

    if (that.authority && !that.isSgApiLink) {
      outputUrl += that.authority;
    }

    outputUrl += that.toRelativeString(false);

    return outputUrl;
  };

  that.toRelativeString = (leadingSlash = true) => {
    let outputUrl = '';
    if (leadingSlash && that.path[0] !== '/') {
      outputUrl = '/';
    }
    if (that.path) {
      outputUrl += encodeURISafe(that.path);
    }
    if (that.query) {
      // .query is always encoded
      outputUrl += `?${that.query}`;
    }

    if (that.fragment) {
      outputUrl += `#${encodeURISafe(that.fragment)}`;
    }

    return outputUrl;
  };

  /**
   * Sets param.
   * @param {string} name name of param
   * @param {string} value value of param
   */
  that.setParam = function (name, value) {
    that.params[name] = value;
    that.setParams();
  };

  /**
   * Sets params array
   *
   * @param {Array} newParams new Params to be set
   */
  that.setParams = function (newParams) {
    const newQueryArr = [];

    if (typeof newParams !== 'undefined') {
      that.params = newParams;
    }

    Object.keys(that.params).forEach((keyName) => {
      newQueryArr.push(`${keyName}=${encodeURIComponentSafe(that.params[keyName])}`);
    });

    that.query = newQueryArr.join('&');
  };

  that.getParam = function (name) {
    if (!(name in that.params)) {
      return undefined;
    }

    return that.params[name];
  };

  /**
   * Checks if current link has coupon param.
   * @return {boolean}
   */
  that.hasCoupon = function () {
    const couponCode = that.getParam('coupon');

    return typeof couponCode !== 'undefined';
  };

  /**
   * Gets a copy of that.params property
   * @return {Object}
   */
  that.getParams = function () {
    return $.extend(true, {}, that.params);
  };

  /**
   * Safely deletes the param.
   *
   * @param {string} name name of param
   * @returns {boolean}
   */
  that.deleteParam = function (name) {
    if (!(name in that.params)) {
      return false;
    }

    delete that.params[name];
    that.setParams(that.params);

    return true;
  };

  /**
   * Safely sets new path of SGLink.
   *
   * @param {string} incomingPath path to set
   * @returns {boolean}
   */
  that.setPath = function (incomingPath) {
    let path = incomingPath;
    if (typeof path !== 'string') {
      logger.info('Path must be string');
      return false;
    }

    if (path[0] !== '/') {
      path = `/${path}`;
    }

    const tmpLink = new SGLink(path);
    that.path = tmpLink.path;
    that.action = tmpLink.action;

    return true;
  };

  /**
   * Gets path.
   * @returns {string}
   */
  that.getPath = function () {
    if (that.path[0] !== '/') {
      return `/${that.path}`;
    }
    return that.path;
  };

  /**
   * Checks if link is an smartAppDownloadLink (starts with /app)
   *
   * @returns {boolean}
   */
  that.isSmartAppDownloadLink = function () {
    if (
      that.path.indexOf('/app/') === 0
      || that.path.indexOf('/app?') === 0
    ) {
      return true;
    }

    return false;
  };

  that.isNonWebViewLink = () => {
    return that.action === 'intercom_chat';
  };

  that.getActionMapping = () => {
    const action = getSanitizedAction();
    return that.actionMapping[action];
  };

  init();

  return that;
};

const checkGlobals = () => {
  if (typeof window !== 'undefined') {
    if (!window.hasOwnProperty('SGLink')) {
      window.SGLink = SGLink;
      return;
    }
    SGLink.prototype.actionMapping = window.SGLink.prototype.actionMapping;
  }
};

checkGlobals();

if (!SGLink.prototype.hasOwnProperty('actionMapping')) {
  /**
   * Action mapping that should be extended for different platforms
   *
   * See implementation in any shopgate-link-extends.js
   */
  SGLink.prototype.actionMapping = {
    default: {
      targetTab: 'main',
      navigationType: 'default',
    },
  };
}


export default SGLink;



// WEBPACK FOOTER //
// ./Library/1.0/common/shopgate-link.js